// firebase/useQuizzes.ts
// Custom hooks for Quiz Creator Parent Portal integration

import { useState, useEffect } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    Timestamp,
    deleteDoc
} from 'firebase/firestore';
import { db } from './config';
import {
    Quiz,
    QuizResponse,
    QuizAnalyticsSummary,
    ChildQuizOverview,
    QuestionStatistics,
    ReviewStatus
} from '../types';

// ============================================
// HELPER FUNCTIONS
// ============================================

const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    if (timestamp instanceof Timestamp) {
        return timestamp.toDate();
    }
    if (timestamp.toDate) {
        return timestamp.toDate();
    }
    return new Date(timestamp);
};

const convertQuizFromFirestore = (data: any): Quiz => {
    return {
        ...data,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
        publishedAt: data.publishedAt ? convertTimestamp(data.publishedAt) : undefined,
        flaggedAt: data.flaggedAt ? convertTimestamp(data.flaggedAt) : undefined,
        reviewedAt: data.reviewedAt ? convertTimestamp(data.reviewedAt) : undefined,
    } as Quiz;
};

const convertQuizResponseFromFirestore = (data: any): QuizResponse => {
    return {
        ...data,
        startedAt: convertTimestamp(data.startedAt),
        completedAt: convertTimestamp(data.completedAt),
    } as QuizResponse;
};

// ============================================
// HOOKS
// ============================================

/**
 * Fetch all quizzes created by a specific child
 */
export const useChildQuizzes = (childProfileID: string) => {
    const [quizzes, setQuizzes] = useState<Quiz[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchQuizzes = async () => {
            try {
                setLoading(true);
                const quizzesRef = collection(db, 'quizzes');
                const q = query(
                    quizzesRef,
                    where('creatorProfileID', '==', childProfileID),
                    orderBy('createdAt', 'desc')
                );

                const snapshot = await getDocs(q);
                const quizData = snapshot.docs.map(doc => ({
                    ...convertQuizFromFirestore(doc.data()),
                    id: doc.id
                })) as Quiz[];

                setQuizzes(quizData);
                setError(null);
            } catch (err) {
                console.error('Error fetching child quizzes:', err);
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        if (childProfileID) {
            fetchQuizzes();
        }
    }, [childProfileID]);

    return { quizzes, loading, error, refetch: () => {} };
};

/**
 * Fetch a single quiz by ID
 */
export const useQuiz = (quizId: string) => {
    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchQuiz = async () => {
            try {
                setLoading(true);
                const quizDoc = await getDoc(doc(db, 'quizzes', quizId));

                if (quizDoc.exists()) {
                    setQuiz({
                        ...convertQuizFromFirestore(quizDoc.data()),
                        id: quizDoc.id
                    } as Quiz);
                } else {
                    setError(new Error('Quiz not found'));
                }
            } catch (err) {
                console.error('Error fetching quiz:', err);
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        if (quizId) {
            fetchQuiz();
        }
    }, [quizId]);

    return { quiz, loading, error };
};

/**
 * Fetch all responses for a specific quiz
 */
export const useQuizResponses = (quizId: string) => {
    const [responses, setResponses] = useState<QuizResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const fetchResponses = async () => {
            try {
                setLoading(true);
                const responsesRef = collection(db, 'quizResponses');
                const q = query(
                    responsesRef,
                    where('quizId', '==', quizId),
                    where('isCompleted', '==', true),
                    orderBy('completedAt', 'desc')
                );

                const snapshot = await getDocs(q);
                const responseData = snapshot.docs.map(doc => ({
                    ...convertQuizResponseFromFirestore(doc.data()),
                    id: doc.id
                })) as QuizResponse[];

                setResponses(responseData);
                setError(null);
            } catch (err) {
                console.error('Error fetching quiz responses:', err);
                setError(err as Error);
            } finally {
                setLoading(false);
            }
        };

        if (quizId) {
            fetchResponses();
        }
    }, [quizId]);

    return { responses, loading, error };
};

/**
 * Calculate quiz analytics from responses
 */
export const useQuizAnalytics = (quizId: string) => {
    const { quiz, loading: quizLoading } = useQuiz(quizId);
    const { responses, loading: responsesLoading } = useQuizResponses(quizId);
    const [analytics, setAnalytics] = useState<QuizAnalyticsSummary | null>(null);

    useEffect(() => {
        if (!quiz || responses.length === 0) {
            setAnalytics(null);
            return;
        }

        // Calculate overall stats
        const totalAttempts = responses.length;
        const totalCompletions = responses.filter(r => r.isCompleted).length;
        const completionRate = totalCompletions / totalAttempts;

        // Calculate score statistics
        const scores = responses.map(r => r.percentageScore);
        const averageScore = scores.reduce((a, b) => a + b, 0) / scores.length;
        const highestScore = Math.max(...scores);
        const lowestScore = Math.min(...scores);
        const sortedScores = [...scores].sort((a, b) => a - b);
        const medianScore = sortedScores[Math.floor(sortedScores.length / 2)];

        // Calculate time statistics
        const times = responses.map(r => r.timeSpent);
        const averageTimeSpent = times.reduce((a, b) => a + b, 0) / times.length;
        const fastestCompletion = Math.min(...times);
        const slowestCompletion = Math.max(...times);

        // Calculate question statistics
        const questionStats: QuestionStatistics[] = quiz.questions.map(question => {
            const questionAttempts = responses.flatMap(r =>
                r.answers.filter(a => a.questionId === question.id)
            );

            const totalAttempts = questionAttempts.length;
            const correctAttempts = questionAttempts.filter(a => a.isCorrect).length;
            const incorrectAttempts = totalAttempts - correctAttempts;
            const accuracyRate = totalAttempts > 0 ? correctAttempts / totalAttempts : 0;

            return {
                questionId: question.id,
                questionText: question.questionText,
                totalAttempts,
                correctAttempts,
                incorrectAttempts,
                accuracyRate,
                averageTimeSpent: 0, // Would need to track per-question time
            };
        });

        // Get recent responses (last 10)
        const recentResponses = responses.slice(0, 10);

        setAnalytics({
            quizId: quiz.id,
            quizTitle: quiz.title,
            totalAttempts,
            totalCompletions,
            completionRate,
            averageScore,
            highestScore,
            lowestScore,
            medianScore,
            averageTimeSpent,
            fastestCompletion,
            slowestCompletion,
            questionStats,
            recentResponses,
            aggregatedAt: new Date(),
        });
    }, [quiz, responses]);

    return {
        analytics,
        loading: quizLoading || responsesLoading
    };
};

/**
 * Get overview of a child's quiz activity
 */
export const useChildQuizOverview = (childProfileID: string) => {
    const { quizzes, loading } = useChildQuizzes(childProfileID);
    const [overview, setOverview] = useState<ChildQuizOverview | null>(null);

    useEffect(() => {
        if (quizzes.length === 0) {
            setOverview(null);
            return;
        }

        const publishedQuizzes = quizzes.filter(q => q.isPublished).length;
        const draftQuizzes = quizzes.filter(q => q.isDraft).length;

        const totalAttemptsByOthers = quizzes.reduce((sum, q) => sum + q.attemptCount, 0);
        const totalCompletionsByOthers = quizzes.reduce((sum, q) => sum + q.completionCount, 0);
        const averageQuizRating = quizzes.reduce((sum, q) => sum + q.averageScore, 0) / quizzes.length;

        const recentQuizzes = quizzes.slice(0, 5);
        const mostPopularQuiz = quizzes.reduce((prev, current) =>
            (current.attemptCount > prev.attemptCount) ? current : prev
        );

        const lastQuizCreated = quizzes.length > 0 ? quizzes[0].createdAt : undefined;
        const publishedQuizzesArray = quizzes.filter(q => q.isPublished);
        const lastQuizPublished = publishedQuizzesArray.length > 0
            ? publishedQuizzesArray[0].publishedAt
            : undefined;

        setOverview({
            childProfileID,
            childUsername: quizzes[0]?.creatorUsername || 'Unknown',
            totalQuizzesCreated: quizzes.length,
            publishedQuizzes,
            draftQuizzes,
            totalAttemptsByOthers,
            totalCompletionsByOthers,
            averageQuizRating,
            recentQuizzes,
            mostPopularQuiz: mostPopularQuiz.attemptCount > 0 ? mostPopularQuiz : undefined,
            lastQuizCreated,
            lastQuizPublished,
        });
    }, [quizzes, childProfileID]);

    return { overview, loading };
};

// ============================================
// MODERATION ACTIONS
// ============================================

/**
 * Flag a quiz for review
 */
export const flagQuiz = async (quizId: string, reason: string, adminEmail: string) => {
    try {
        const quizRef = doc(db, 'quizzes', quizId);
        await updateDoc(quizRef, {
            reviewStatus: 'flagged',
            flaggedReason: reason,
            flaggedAt: Timestamp.now(),
            reviewedBy: adminEmail,
            reviewedAt: Timestamp.now(),
        });
        return { success: true };
    } catch (error) {
        console.error('Error flagging quiz:', error);
        return { success: false, error };
    }
};

/**
 * Approve a quiz
 */
export const approveQuiz = async (quizId: string, adminEmail: string) => {
    try {
        const quizRef = doc(db, 'quizzes', quizId);
        await updateDoc(quizRef, {
            reviewStatus: 'approved',
            reviewedBy: adminEmail,
            reviewedAt: Timestamp.now(),
            flaggedReason: null,
            flaggedAt: null,
        });
        return { success: true };
    } catch (error) {
        console.error('Error approving quiz:', error);
        return { success: false, error };
    }
};

/**
 * Delete a quiz
 */
export const deleteQuiz = async (quizId: string) => {
    try {
        const quizRef = doc(db, 'quizzes', quizId);
        await deleteDoc(quizRef);
        return { success: true };
    } catch (error) {
        console.error('Error deleting quiz:', error);
        return { success: false, error };
    }
};

/**
 * Update quiz review status
 */
export const updateQuizReviewStatus = async (
    quizId: string,
    status: ReviewStatus,
    adminEmail: string,
    reason?: string
) => {
    try {
        const quizRef = doc(db, 'quizzes', quizId);
        const updateData: any = {
            reviewStatus: status,
            reviewedBy: adminEmail,
            reviewedAt: Timestamp.now(),
        };

        if (status === 'flagged' && reason) {
            updateData.flaggedReason = reason;
            updateData.flaggedAt = Timestamp.now();
        } else {
            updateData.flaggedReason = null;
            updateData.flaggedAt = null;
        }

        await updateDoc(quizRef, updateData);
        return { success: true };
    } catch (error) {
        console.error('Error updating quiz review status:', error);
        return { success: false, error };
    }
};
